import { geoCentroid, geoMercator, geoPath } from "d3-geo";
import type { FeatureCollection, Geometry } from "geojson";
import { MapSurface } from "./MapSurface";
import { MapData, Region, RegionProps } from "../types";

export class PlanarSurface implements MapSurface {
  private width = 1;
  private height = 1;
  private padding = 24;
  private projection = geoMercator();

  constructor(width: number, height: number, padding = 24) {
    this.width = width;
    this.height = height;
    this.padding = padding;
  }

  setSize(width: number, height: number): void {
    this.width = Math.max(1, width);
    this.height = Math.max(1, height);
  }

  project(lonLat: [number, number]): [number, number] {
    const projected = this.projection(lonLat);
    return projected ? (projected as [number, number]) : [0, 0];
  }

  rebuildPaths(data: MapData): {
    regions: Region[];
    featureCollection: FeatureCollection<Geometry, RegionProps>;
  } {
    const pathGenerator = geoPath(this.projection);
    this.projection.fitExtent(
      [
        [this.padding, this.padding],
        [this.width - this.padding, this.height - this.padding],
      ],
      data.featureCollection,
    );
    const spherePath = pathGenerator({ type: "Sphere" });

    const regions = data.regions.map((region) => {
      let pathString = pathGenerator(region.feature) ?? "";
      if (spherePath && pathString.endsWith(spherePath)) {
        pathString = pathString.slice(0, -spherePath.length);
      }
      const path = new Path2D(pathString);
      const bounds = this.computeProjectedBounds(region.feature.geometry);
      const geoCenter = geoCentroid(region.feature);
      const projectedCenter = this.projection(
        geoCenter as [number, number],
      ) ?? [0, 0];
      const centroid = projectedCenter as [number, number];

      return {
        ...region,
        path,
        bbox: {
          minX: bounds.minX,
          minY: bounds.minY,
          maxX: bounds.maxX,
          maxY: bounds.maxY,
        },
        centroid,
      };
    });

    return { regions, featureCollection: data.featureCollection };
  }

  private computeProjectedBounds(geometry: Geometry): {
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
  } {
    let minX = Number.POSITIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;

    const update = (lon: number, lat: number) => {
      const projected = this.projection([lon, lat]);
      if (!projected) {
        return;
      }
      minX = Math.min(minX, projected[0]);
      minY = Math.min(minY, projected[1]);
      maxX = Math.max(maxX, projected[0]);
      maxY = Math.max(maxY, projected[1]);
    };

    const walk = (coords: any): void => {
      if (typeof coords[0] === "number") {
        update(coords[0], coords[1]);
        return;
      }
      for (const item of coords) {
        walk(item);
      }
    };

    if (geometry.type === "GeometryCollection") {
      for (const part of geometry.geometries) {
        if ("coordinates" in part) {
          walk((part as Geometry & { coordinates: any }).coordinates);
        }
      }
    } else {
      walk((geometry as Geometry & { coordinates: any }).coordinates);
    }

    if (!Number.isFinite(minX)) {
      minX = 0;
      minY = 0;
      maxX = 0;
      maxY = 0;
    }

    return { minX, minY, maxX, maxY };
  }
}
